import { useEffect, useState, useCallback } from 'react'
import { orpc } from '@/lib/orpc-client'
import { useProjectEvent } from './presence-context'

/**
 * One meeting, live (PLAN-081): the ProjectStore row over RPC, refetched when
 * the project hub says it changed (an edit by someone else, the bot's status,
 * generated notes landing). Edits go through `meetings.update`.
 */

export type MeetingData = Awaited<ReturnType<typeof orpc.meetings.get>>

export interface MeetingPatch {
  title?: string
  description?: string | null
  startAt?: string
  endAt?: string
  meetingUrl?: string | null
}

export function useMeetingCollab(projectId: string, meetingId: string) {
  const [meeting, setMeeting] = useState<MeetingData | null>(null)
  const [synced, setSynced] = useState(false)

  const reload = useCallback(async () => {
    try {
      setMeeting(await orpc.meetings.get({ projectId, meetingId }))
    } catch (err) {
      console.error('[meeting] load failed', err)
      setMeeting(null)
    } finally {
      setSynced(true)
    }
  }, [projectId, meetingId])

  useEffect(() => {
    setSynced(false)
    setMeeting(null)
    void reload()
  }, [reload])

  useProjectEvent(['meeting:changed', 'meetings:changed'], (event) => {
    if (event.t === 'meeting:changed' && event.meetingId !== meetingId) return
    void reload()
  })

  const updateMeeting = useCallback(
    async (patch: MeetingPatch) => {
      setMeeting((prev) =>
        prev ? { ...prev, ...patch, updatedAt: new Date().toISOString() } : prev
      )
      try {
        setMeeting(await orpc.meetings.update({ projectId, meetingId, ...patch }))
      } catch (err) {
        console.error('[meeting] update failed', err)
        void reload()
      }
    },
    [projectId, meetingId, reload]
  )

  return { meeting, updateMeeting, synced }
}
